import { Component, OnDestroy, OnInit } from '@angular/core';
import { Client, SessionService } from '../../services/session.service';
import { Router } from '@angular/router';
import moment from 'moment';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ConfirmModalComponent } from '../../modals/confirm-modal/confirm-modal.component';
import { AuthService } from '../../services/auth.service';
import { SocketGroup, SocketService } from '../../services/socket.service';
import { DownloadService } from '../../services/download.service';
import { IConfirmModalData } from '../../modals/confirm-modal/models/confirmModalData';
import { IConfirmModalAnswer } from '../../modals/confirm-modal/models/confirmModalAnswer';
import { InfoModalComponent } from '../../modals/info-modal/info-modal.component';
import { IInfoModalData } from '../../modals/info-modal/models/infoModalData';
import { MixpanelService } from '../../services/mixpanel.service';
import { Observable, Subscription } from 'rxjs';
import { MixpanelEvents } from '../../models/mixpanelEvents';
import { BlockedFunctionalService } from '../../services/blocked-functional.service';
import { FunctionalType } from '../../models/blockedFunctionalType';
import { SidePanelService } from '../../services/side-panel.service';
import { ServiceFeatures } from '../../models/serviceFeatures';
import { PlatformApplications } from '../../models/platformApplications';
import { IApplication } from '../../models/application';
import { Languages } from '../../models/languages';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { AIAssistantService, ChatAvailability } from '../../services/ai-assistant.service';
import { AIAssistantOpenerService } from '../../services/ai-assistant-opener.service';
import { SnackBarService } from '../../services/snack-bar.service';

import { SHARED_MODULES } from '../../../shared.';
import { ActiveRoutePipe } from '../../pipes/active-route.pipe';
import { GetLinkPipe } from '../../pipes/get-link.pipe';
import { HasAccessPipe } from '../../pipes/has-access.pipe';
import { HasFeaturePipe } from '../../pipes/has-feature.pipe';
import { SanitizerPipe } from '../../pipes/sanitizer.pipe';
import { AutocompleteComponent } from '../autocomplete/autocomplete.component';
import { DownloadMenuComponent } from '../download-menu/download-menu.component';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { PersonalSettingsComponent } from '../personal-settings/personal-settings.component';
import { PhButtonComponent } from '../ph-button/ph-button.component';
import { PhIconComponent } from '../ph-icon/ph-icon.component';
import { PhIconWrapperComponent } from '../ph-icon-wrapper/ph-icon-wrapper.component';
import { PhLinkButtonComponent } from '../link-button/ph-link-button.component';
import { PhNotificationsComponent } from '../notifications/ph-notifications/ph-notifications.component';
import { SidePanelComponent } from '../side-panel/side-panel.component';
import { TimersMenuComponent } from '../timers-menu/timers-menu.component';
import { UserLogoGenerateComponent } from '../user-logo-generate/user-logo-generate.component';
import { AllPlanogramChatsWidgetComponent } from '../all-planogram-chats-widget/all-planogram-chats-widget.component';

const TIME_FORMATTER: string = 'HH:mm';

enum DescriptionMapper {
	BENTO_MAIN_DESCRIPTION = 'APPS_SWITCHER.MAIN_DESCRIPTION',
	BENTO_BI_DESCRIPTION = 'APPS_SWITCHER.BI_DESCRIPTION',
	BENTO_BUILDER_DESCRIPTION = 'APPS_SWITCHER.BUILDER_DESCRIPTION',
	BENTO_AP_DESCRIPTION = 'APPS_SWITCHER.AP_DESCRIPTION',
	BENTO_DC_DESCRIPTION = 'APPS_SWITCHER.DC_DESCRIPTION',
	UPLOAD_DATA = 'APPS_SWITCHER.CONNECTOR_DESCRIPTION'
}

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
	imports: [
		SHARED_MODULES,
		ActiveRoutePipe,
		GetLinkPipe,
		HasAccessPipe,
		HasFeaturePipe,
		SanitizerPipe,
		AutocompleteComponent,
		DownloadMenuComponent,
		FaIconComponent,
		PersonalSettingsComponent,
		PhButtonComponent,
		PhIconComponent,
		PhIconWrapperComponent,
		PhLinkButtonComponent,
		PhNotificationsComponent,
		SidePanelComponent,
		TimersMenuComponent,
		UserLogoGenerateComponent,
		AllPlanogramChatsWidgetComponent
	],
    providers: [SidePanelService],
    standalone: true
})
export class HeaderComponent implements OnInit, OnDestroy {

	apps: Array<IApplication> = [];
	clientId: number = -1;
	clientList: Array<Client>;
	daysLeft: Observable<number>;
	defaultRoute: { title: string, route: string };
	hasDemoAccess: boolean = false;
	language: Languages;
	languageChangeShown: boolean = false;
	languageChangeSubscription: Subscription;
	languages: Array<{ key: Languages, title: string }>;
	languagesEmojis: Map<Languages, string>;
	mainApps: Array<IApplication> = [];
	paymentDeclined: boolean = false;
	settingsPanelOpened: boolean = false;
	tariffEndDate: Date = new Date();
	userSettings: any = [];
	isSystemAvailable: boolean = false;

	protected readonly features: typeof ServiceFeatures = ServiceFeatures;
	protected readonly functionalType: typeof FunctionalType = FunctionalType;

	static getNextPage(str: string): string {
		return str ? str.split('?page=').slice(-1)[0] : null;
	}
	
	static formatTime(item): void {
		item.time = moment(item.created_at).format(TIME_FORMATTER);
	}

	static sortByDate(a, b): number {
		const aDate: Date = new Date(a.created_at),
			bDate: Date = new Date(b.created_at);

		return Number(bDate) - Number(aDate);
	}

	constructor(
		private dialog: MatDialog,
		private translate: TranslateService,
		private aiAssistantService: AIAssistantService,
		private aiAssistantOpenerService: AIAssistantOpenerService,
		private authService: AuthService,
		private socketService: SocketService,
		private downloadService: DownloadService,
		private mixPanelService: MixpanelService,
		private blockedFunctionalService: BlockedFunctionalService,
		public router: Router,
		public sessionService: SessionService,
		private snackBarService: SnackBarService
	) {
		this.languageChangeShown = SessionService.isStaff(this.sessionService.defaults.email);
		if (this.languageChangeShown) {
			this.language = this.translate.currentLang as Languages;
			this.languageChangeSubscription = this.translate.onLangChange.subscribe(
				(value: LangChangeEvent) => this.language = value.lang as Languages
			);
			this.languagesEmojis = new Map<Languages, string>([
				[Languages.Ukrainian, '🇺🇦'],
				[Languages.English, '🇬🇧'],
				[Languages.Spanish, '🇪🇸'],
				[Languages.SimplifiedChinese, '🇨🇳'],
				[Languages.TraditionalChinese, '🇨🇳'],
				[Languages.Japanese, '🇯🇵'],
				[Languages.Arabic, '🇸🇦'],
				[Languages.Polish, '🇵🇱'],
				[Languages.Turkish, '🇹🇷'],
				[Languages.Lithuanian, '🇱🇹'],
				[Languages.Korean, '🇰🇷'],
				[Languages.Thai, '🇹🇭'],
				[Languages.Indonesian, '🇮🇩'],
				[Languages.Italian, '🇮🇹'],
				[Languages.Vietnamese, '🇻🇳'],
				[Languages.Greek, '🇬🇷'],
				[Languages.Georgian, '🇬🇪'],
				[Languages.Serbian, '🇷🇸'],
				[Languages.Bulgarian, '🇧🇬'],
				[Languages.Azerbaijani, '🇦🇿']
			]);
			this.languages = Object.keys(Languages)
				.filter((lang: string) => Languages[lang] !== 'ba')
				.map((lang: string) => ({
					key: Languages[lang] as Languages,
					title: lang +
						(this.languagesEmojis.has(Languages[lang])
							? ` ${this.languagesEmojis.get(Languages[lang])}`
							: ''
						)
				}));
		}

		this.defaultRoute = this.sessionService.defaultRoute;
		this.hasDemoAccess = this.sessionService.hasFeature(ServiceFeatures.Demo);
		this.daysLeft = this.sessionService.demoDaysLeft$.asObservable();
		this.paymentDeclined = this.sessionService.defaults.tariff_plan.last_auto_payment_declined
			&& this.sessionService.hasAccess('ap-payments-and-subscriptions');
		this.tariffEndDate = new Date(this.sessionService.defaults.tariff_plan.active_date_to);
		this.tariffEndDate.setDate(
			this.tariffEndDate.getDate() + (this.sessionService.defaults.tariff_plan.extra_access_days ?? 0)
		);
		this.sessionService.apps.forEach((app: IApplication) => {
			app.bento_menu_description = DescriptionMapper[app.bento_menu_description];
		})
		this.apps = this.sessionService.apps.filter((app: IApplication) => !app.is_main);
		this.mainApps = this.sessionService.apps.filter((app: IApplication) => app.is_main);

		this.clientList = [...this.sessionService.clients];
		this.sessionService.clientIdChange.subscribe(async (clientId: number) => {
			if (this.clientId != clientId) {
				this.clientId = clientId;
				// global sockets subscribing
				this.socketService.addSubscription(SocketGroup.SyncProducts);
				if (this.sessionService.defaults.isManager && this.sessionService.defaults.planogramId)
					this.socketService.addSubscription(SocketGroup.TimeTracker);

				if (!this.hasDemoAccess) {
				} else {
					this.socketService.addSubscription(SocketGroup.DemoHomePage);
				}
			}
		});

		this.userSettings.push({
			title: '',
			items: [
				{
					icon: 'bes',
					name: 'USER_PANEL.PROFILE',
					description: 'USER_PANEL.PROFILE_DESCRIPTION',
					click: () => {
						window.open(
							this.sessionService.getExternalLink(PlatformApplications.BES, '/account/profile'),
							'_blank'
						);
					}
				},
				{
					icon: 'planohero',
					name: 'USER_PANEL.PERSONAL_SETTINGS',
					description: 'USER_PANEL.PERSONAL_SETTINGS_DESCRIPTION',
					click: () => {
						this.settingsPanelOpened = true;
					}
				}
			]
		});

		const adminPanelGroup: Array<any> = [];
		if (this.sessionService.hasAccess('tariff-plans')) {
			adminPanelGroup.push({
				icon: 'tariffs',
				name: 'USER_PANEL.TARIFFS',
				description: 'USER_PANEL.TARIFFS_DESCRIPTION',
				click: () => {
					window.open(
						this.sessionService.getExternalLink(PlatformApplications.AdminPanel, '/tariff-plans'),
						'_blank'
					);
				}
			});
		}
		if (this.sessionService.hasAccess('ap-payments-and-subscriptions')) {
			adminPanelGroup.push({
				icon: 'payments',
				name: 'USER_PANEL.PAYMENT_AND_SUBSCRIPTIONS',
				description: 'USER_PANEL.PAYMENT_AND_SUBSCRIPTIONS_DESCRIPTION',
				click: () => {
					window.open(
						this.sessionService.getExternalLink(PlatformApplications.AdminPanel, '/subscriptions'),
						'_blank'
					);
				}
			});
		}
		if (this.sessionService.hasAccess('roles')) {
			adminPanelGroup.push({
				icon: 'roles',
				name: 'USER_PANEL.ROLES',
				description: 'USER_PANEL.ROLES_DESCRIPTION',
				click: () => {
					window.open(
						this.sessionService.getExternalLink(PlatformApplications.AdminPanel, '/roles'),
						'_blank'
					);
				}
			});
		}
		if (this.sessionService.hasAccess('users')) {
			adminPanelGroup.push({
				icon: 'users',
				name: 'USER_PANEL.USERS',
				description: 'USER_PANEL.USERS_DESCRIPTION',
				click: () => {
					window.open(
						this.sessionService.getExternalLink(PlatformApplications.AdminPanel, '/users'),
						'_blank'
					);
				}
			});
		}
		if (adminPanelGroup.length) {
			this.userSettings.push({
				title: 'Admin Panel',
				items: adminPanelGroup
			});
		}

		const connectorGroup: Array<any> = [];
		if (this.sessionService.hasAccess('profile-allowed-connector')) {
			connectorGroup.push({
				icon: 'connector',
				name: 'USER_PANEL.CONNECTOR',
				description: 'USER_PANEL.CONNECTOR_DESCRIPTION',
				click: () => {
					window.open(
						this.sessionService.getExternalLink(PlatformApplications.Connector),
						'_blank'
					);
				}
			})
		}
		if (connectorGroup.length) {
			this.userSettings.push({
				title: 'Connector',
				items: connectorGroup
			});
		}

		this.aiAssistantService.getAvailability().subscribe((result: ChatAvailability) => {
			this.aiAssistantService.clientAccess = result.client_access;
			this.aiAssistantService.clientAccessActive = result.client_access_active;
			this.aiAssistantService.limitInfo = result.limit_info;
			this.aiAssistantService.privacyPolicyAccepted = result.privacy_policy_accepted;
			this.aiAssistantService.profileAccess = result.profile_access;
			this.aiAssistantService.seatsOverused = result.seats_overused;
		});
	}

	async ngOnInit() {
		this.isSystemAvailable = SessionService.isPH(this.sessionService.username) || SessionService.isNIQ(this.sessionService.username);

		this.sessionService.personalSettingsShownSubject.subscribe((value: boolean) => {
			this.settingsPanelOpened = value;
		});
	}

	chooseTariff(): void {
		this.mixPanelService.trackPage(MixpanelEvents.ChooseTariff, { page: 'Header' });
		window.open(this.sessionService.getExternalLink(PlatformApplications.AdminPanel, '/tariff-plans'), '_blank');
	}

	clearSession(): void {
		this.sessionService.clearSession();
		this.authService.logout();
	}

	logOut(): void {
		this.clearSession();
	}

	onChangeClient(clientId: number): void {
		this.sessionService.setUrlClientId(this.clientId, clientId);
		window.location.reload();
	}

	openApp(app: IApplication): void {
		const url: string = `${app.host}${app.path ?? ''}`;
		window.open(url.replace(':client_id', this.sessionService.clientId.toString()), '_blank');
	}

	openBlockedModal(type: FunctionalType): void {
		this.blockedFunctionalService.openBlockedFunctionalModal(type);
	}

	payTariff(): void {
		this.mixPanelService.trackPage(MixpanelEvents.PayTariff, { page: 'Header' });
		window.open(this.sessionService.getExternalLink(PlatformApplications.AdminPanel, '/subscriptions'), '_blank');
	}

	onLanguageChange(language: Languages): void {
		this.translate.use(language);
		this.sessionService.updateLanguage(language).subscribe();
	}

	openHelpdesk(): void {
		const links = {
			en: 'https://helpdesk.planohero.com/en/',
			uk: 'https://helpdesk.planohero.com/uk/',
			ru: 'https://helpdesk.planohero.com/ru/',
			pl: 'https://helpdesk.planohero.com/pl/'
		};

		this.mixPanelService.trackPage(MixpanelEvents.Helpdesk);
		window.open(links[this.translate.currentLang] ?? links.en, '_blank');
	}

	openWhatNew(): void {
		const links = {
			en: 'https://helpdesk.planohero.com/en/category/whats-new-1ssmf4d/',
			uk: 'https://helpdesk.planohero.com/uk/category/sho-novogo-1fpt9lb/',
			ru: 'https://helpdesk.planohero.com/ru/category/chto-novogo-1t6uawh/'
		};

		this.mixPanelService.trackPage(MixpanelEvents.WhatNew);
		window.open(links[this.translate.currentLang] ?? links.en, '_blank');
	}

	openChat(): void {
		if (!this.aiAssistantService.clientAccess) {
			const media = this.sessionService.media.wizora_intro;
			this.dialog
				.open<InfoModalComponent, IInfoModalData<InfoModalComponent>>(InfoModalComponent, {
					width: '60vw',
					height: '80vh',
					autoFocus: false,
					backdropClass: 'back-drop',
					panelClass: 'full-width-modal-actions',
					data: {
						media: media.url,
						mediaType: media.media_type,
						type: 'INFO_MODAL.ADDITIONAL_FEATURES',
						title: 'AI_ASSISTANT.NO_ACCESS_TITLE',
						description: 'AI_ASSISTANT.NO_ACCESS_DESCRIPTION',
						secondaryButtonText: 'GO_BACK',
						secondaryButtonCallback: (ref: MatDialogRef<InfoModalComponent>) => {
							ref.close();
						},
						primaryButtonText: 'COMMON.LEAVE_REQUEST',
						primaryButtonCallback: (ref: MatDialogRef<InfoModalComponent>) => {
							this.aiAssistantService.leaveRequest().subscribe((response: { status: boolean }) => {
								if (response.status) {
									this.snackBarService.setSnackBarNotification({
										messageText: 'COMMON.REQUEST_SENT'
									});
								}
							});
							ref.close();
						}
					}
				})
				.afterClosed();
		} else if (!this.aiAssistantService.profileAccess) {
			const media = this.sessionService.media.wizora_intro;
			this.dialog
				.open<InfoModalComponent, IInfoModalData<InfoModalComponent>>(InfoModalComponent, {
					width: '60vw',
					height: '80vh',
					autoFocus: false,
					backdropClass: 'back-drop',
					panelClass: 'full-width-modal-actions',
					data: {
						media: media.url,
						mediaType: media.media_type,
						type: 'INFO_MODAL.ADDITIONAL_FEATURES',
						title: 'AI_ASSISTANT.NO_ACCESS_TITLE',
						description: this.sessionService.defaults.isAdmin ? 'AI_ASSISTANT.NO_ACCESS_DESCRIPTION' : 'AI_ASSISTANT.NO_ACCESS_DESCRIPTION_USER',
						primaryButtonText: 'COMMON.OK',
						primaryButtonCallback: (ref: MatDialogRef<InfoModalComponent>) => {
							ref.close();
						}
					}
				})
				.afterClosed();
		} else {
			this.aiAssistantOpenerService.openChat(50);
		}
	}

	ngOnDestroy(): void {
		if (this.languageChangeSubscription) this.languageChangeSubscription.unsubscribe();
	}
}
